import { useState } from "react"

import { useCommonUseOfFundsListSuspense } from "@boundless/bl-api-client/common/services"
import {
  type Company,
  type CustomerFundingRequests,
  FundingNeedsStatusEnum,
} from "@boundless/bl-api-client/customer/models"
import {
  useCustomerApplicationsRetrieve,
  useCustomerCompanyRetrieveSuspense,
} from "@boundless/bl-api-client/customer/services"
import { Link } from "@tanstack/react-router"
import { DownloadIcon } from "lucide-react"

import { DownloadButton } from "@/components/documents/DownloadButton"
import { PendingComponent } from "@/components/error/PendingComponent"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatCurrency } from "@/lib/forms/utils"
import { getCurrentStepPath } from "@/lib/utils"

function SignBoundlessApplicationCTA({
  company,
  fundingNeeds,
  latestApplicationUuid,
  onExpiredClick,
}: {
  company: Company
  fundingNeeds: CustomerFundingRequests | undefined
  latestApplicationUuid: string | null
  onExpiredClick: () => void
}) {
  if (!company || !company.isSubmitted) {
    if (!company?.currentStep) {
      return <Link to="/company/details">Complete Business Profile</Link>
    }
    const currentStepPath = getCurrentStepPath(
      company.currentStep,
      company.latestUnsubmittedApplicationUuid,
      company.latestSubmittedFundingNeedsUuid,
    )
    return (
      <Link {...currentStepPath} className="text-primary hover:underline">
        Complete Business Profile
      </Link>
    )
  }

  if (fundingNeeds?.status === FundingNeedsStatusEnum.expired) {
    return (
      <button
        onClick={onExpiredClick}
        className="text-primary cursor-pointer hover:underline"
      >
        Sign Application
      </button>
    )
  }

  if (fundingNeeds?.isSigned && fundingNeeds.applicationDocumentDownloadUrl) {
    return (
      <DownloadButton
        filename="boundless-application.pdf"
        url={fundingNeeds.applicationDocumentDownloadUrl}
        className="text-primary hover:underline"
      >
        Download Application
      </DownloadButton>
    )
  }

  if (fundingNeeds === undefined || !fundingNeeds.isReadyToBeSigned) {
    const currentStepPath = getCurrentStepPath(
      company.currentStep,
      company.latestUnsubmittedApplicationUuid,
      company.latestSubmittedFundingNeedsUuid,
    )
    return (
      <Link {...currentStepPath} className="text-primary hover:underline">
        Complete Funding Request
      </Link>
    )
  }

  if (
    fundingNeeds &&
    fundingNeeds.isReadyToBeSigned &&
    !!latestApplicationUuid
  ) {
    return (
      <Link
        className="text-primary hover:underline"
        to="/applications/$applicationUuid/sign"
        search={{
          returnTo: "/documents",
        }}
        params={{
          applicationUuid: latestApplicationUuid,
        }}
      >
        Sign Application
      </Link>
    )
  }

  return <span>No Active Application</span>
}

export function SignBoundlessApplicationCard({
  companyUuid,
}: {
  companyUuid: string
}) {
  const { data: company } = useCustomerCompanyRetrieveSuspense(companyUuid)
  const [showExpiredModal, setShowExpiredModal] = useState(false)

  const latestApplicationUuid = company.latestUnsubmittedApplicationUuid
  const { data: latestUnsubmittedApplicationData, isLoading } =
    useCustomerApplicationsRetrieve(companyUuid, latestApplicationUuid ?? "", {
      query: {
        enabled: !!latestApplicationUuid,
      },
    })
  const { data: useOfFundsData } = useCommonUseOfFundsListSuspense()

  if (isLoading) {
    return <PendingComponent />
  }

  const fn = latestUnsubmittedApplicationData?.fundingNeeds
  const useOfFundsName = fn
    ? useOfFundsData.find((fund) => fund.id === fn.useOfFunds)?.name
    : undefined

  return (
    <>
      <Card className="w-full border-0 bg-gray-50 shadow-sm">
        <CardHeader>
          <CardTitle>Boundless Application</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-background flex flex-col gap-3 rounded-2xl p-4 lg:flex-row lg:items-center lg:justify-between lg:gap-0">
            <div className="min-w-0">
              <SignBoundlessApplicationCTA
                company={company}
                fundingNeeds={fn}
                latestApplicationUuid={latestApplicationUuid}
                onExpiredClick={() => setShowExpiredModal(true)}
              />
            </div>

            <div className="flex w-full flex-row items-center justify-between gap-3 lg:w-auto lg:justify-start">
              <span className="whitespace-nowrap">
                {fn ? formatCurrency(fn.capitalAmount.toString()) : ""}
              </span>
              <span className="min-w-0 flex-1 truncate lg:flex-none lg:truncate">
                {useOfFundsName}
              </span>
              <span className="shrink-0">
                {fn && fn.isSigned && fn.applicationDocumentDownloadUrl ? (
                  <DownloadButton
                    aria-label="Download signed application"
                    filename="boundless-application.pdf"
                    url={fn.applicationDocumentDownloadUrl}
                    className="text-primary hover:underline"
                    title="Download signed application"
                  >
                    <DownloadIcon className="h-5 w-5" />
                  </DownloadButton>
                ) : (
                  <DownloadIcon className="h-5 w-5 text-gray-400" />
                )}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expired modal */}
      <Dialog open={showExpiredModal} onOpenChange={setShowExpiredModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl">
              Funding Request Expired
            </DialogTitle>
            <DialogDescription className="text-lg">
              Your funding request has expired.
              <br />
              Please create a new funding request{" "}
              <Link
                to="/funding-requests/create"
                className="text-primary hover:underline"
                onClick={() => setShowExpiredModal(false)}
              >
                here
              </Link>
              .
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  )
}
